"use client";
import useDates from "@/hooks/useDates";
import Section from "../components/Section";
import { Box, Button, Grid2, Popover, Typography } from "@repo/ui";
import {
  CalendarIcon,
  LocationIcon,
  ShareIcon,
  TimeIcon,
  UserOutlined,
  UsersOutline,
} from "@spoctech/icons";
import Image from "next/image";
import { useEffect, useState } from "react";
import BreadcrumbsLink from "@/components/common/BreadcrumbsLink";
import SocialShare from "@/components/common/SocialShare";

type WebinarBannerProps = {
  sectionInfo: PageSections;
  data: {
    description: string;
    webinarBanner: {
      heading: string;
      eventDates: string[];
      imagePath: string;
      joinThrough: string;
      numberOfLearners?: string;
      eventDetail: {
        startTime: string;
        endTime: string;
      };
    };
    breadcrumbs: BreadcrumbsType[];
  };
};

const WebinarBanner = ({ sectionInfo, data }: WebinarBannerProps) => {
  const { formattedDate } = useDates(data?.webinarBanner?.eventDates);
  const { startTime, endTime } = data.webinarBanner.eventDetail;

  const formattedTime = `${startTime} - ${endTime}`;
  const [authorName, setAuthorName] = useState<string | null>(null);

  useEffect(() => {
    const el = document.querySelector("[data-author-name]");
    if (el instanceof HTMLElement) {
      setAuthorName(el.dataset.authorName || null);
    }
  }, []);

  return (
    <Section
      id={sectionInfo.sectionId}
      color="primary"
      className={`${sectionInfo.sectionClass} relative`}
    >
      <BreadcrumbsLink
        breadcrumbs={data.breadcrumbs}
        className="absolute top-4"
      />

      <Grid2 container spacing={8} className="pt-4" align="center">
        <Grid2 size={{ sm: 12, md: 4, lg: 4 }}>
          {data?.webinarBanner?.imagePath && (
            <Image
              src={data?.webinarBanner?.imagePath}
              alt={data?.webinarBanner?.heading}
              width={433}
              height={316}
              className="rounded-lg w-full h-auto"
              priority
            />
          )}
        </Grid2>
        <Grid2 size={{ sm: 12, md: 8, lg: 8 }} className="text-white">
          <Typography variant="h1">{data.webinarBanner.heading}</Typography>
          <Typography paragraph className="pt-4">
            {data.description}
          </Typography>
          <Box className="grid gap-4 sm:grid-cols-3 grid-cols-2">
            <div className="flex gap-2 items-center text-sm">
              <UserOutlined className="size-4 flex-shrink-0" />
              {authorName ?? "Loading..."}
            </div>
            <div className="flex gap-2 items-center text-sm">
              <CalendarIcon className="size-4 flex-shrink-0" /> {formattedDate}
            </div>
            <div className="flex gap-2 items-center text-sm">
              <TimeIcon className="size-4 flex-shrink-0" /> {formattedTime}
            </div>
            <div className="flex gap-2 items-center text-sm">
              <LocationIcon className="size-4 flex-shrink-0" />{" "}
              {data.webinarBanner.joinThrough}
            </div>
            <div className="flex gap-2 items-center text-sm">
              <UsersOutline className="size-4 flex-shrink-0" />
              {data.webinarBanner?.numberOfLearners || "166+"} Members
            </div>
          </Box>

          <Box className="pt-4">
            <Popover
              side="right"
              trigger={
                <Button
                  variant="outlined"
                  aria-label="share"
                  icon={<ShareIcon />}
                />
              }
            >
              <SocialShare />
            </Popover>
          </Box>
        </Grid2>
      </Grid2>
    </Section>
  );
};

export default WebinarBanner;
